import { useState, useEffect, useRef, useCallback } from 'react';
import Supercluster from 'supercluster';
import { getInfraStatus, getInfraId } from '@/utils/infraStatus';

export interface ClusterPoint {
	id: number;
	longitude: number;
	latitude: number;
	count: number;
	hasAlert: boolean;
	normalCount: number;
	criticalCount: number;
	noDataCount: number;
	selectedCount: number;
	clusterId: number;
}

export interface UnclusteredPoint {
	infrastructure: any;
}

export interface ClusteringResult {
	clusters: ClusterPoint[];
	unclustered: UnclusteredPoint[];
	getExpansionZoom: (clusterId: number) => number;
}

interface SuperclusterProperties {
	infraId: number;
	status: 'normal' | 'critical' | 'noData';
	isSelected: boolean;
	index: number;
}

const CLUSTER_RADIUS = 60;
const CLUSTER_MIN_ZOOM = 0;
const CLUSTER_MAX_ZOOM = 16;
const WORLD_BOUNDS: [number, number, number, number] = [-180, -85, 180, 85];

const EMPTY_RESULT: ClusteringResult = {
	clusters: [],
	unclustered: [],
	getExpansionZoom: () => 14,
};

export function useInfrastructureClustering(
	filteredInfras: any[] | null,
	zoom: number,
	selectedInfraIds?: Set<number>,
): ClusteringResult {
	const indexRef = useRef<Supercluster<SuperclusterProperties> | null>(null);
	const [result, setResult] = useState<ClusteringResult>(EMPTY_RESULT);

	useEffect(() => {
		if (!filteredInfras || filteredInfras.length === 0) {
			indexRef.current = null;
			setResult(EMPTY_RESULT);
			return;
		}

		const features: GeoJSON.Feature<GeoJSON.Point, SuperclusterProperties>[] = filteredInfras.map((infra, idx) => {
			const infraId = getInfraId(infra);
			return {
				type: 'Feature' as const,
				geometry: {
					type: 'Point' as const,
					coordinates: [Number.parseFloat(infra.longitude as string), Number.parseFloat(infra.latitude as string)],
				},
				properties: {
					infraId: infraId as number,
					status: getInfraStatus(infra),
					isSelected: infraId != null && (selectedInfraIds?.has(infraId) ?? false),
					index: idx,
				},
			};
		});

		const index = new Supercluster<SuperclusterProperties>({
			radius: CLUSTER_RADIUS,
			minZoom: CLUSTER_MIN_ZOOM,
			maxZoom: CLUSTER_MAX_ZOOM,
			map: (props) => ({
				normalCount: props.status === 'normal' ? 1 : 0,
				criticalCount: props.status === 'critical' ? 1 : 0,
				noDataCount: props.status === 'noData' ? 1 : 0,
				selectedCount: props.isSelected ? 1 : 0,
				totalCount: 1,
			}),
			reduce: (accumulated: any, props: any) => {
				accumulated.normalCount += props.normalCount;
				accumulated.criticalCount += props.criticalCount;
				accumulated.noDataCount += props.noDataCount;
				accumulated.selectedCount += props.selectedCount;
				accumulated.totalCount += props.totalCount;
			},
		});

		index.load(features);
		indexRef.current = index;

		const clusterZoom = Math.floor(zoom);
		let rawClusters: any[];
		try {
			rawClusters = index.getClusters(WORLD_BOUNDS, clusterZoom);
		} catch {
			setResult(EMPTY_RESULT);
			return;
		}

		const clusters: ClusterPoint[] = [];
		const unclustered: UnclusteredPoint[] = [];

		for (const feature of rawClusters) {
			const props = feature.properties;
			if (props.cluster) {
				const criticalCount = props.criticalCount ?? 0;
				const normalCount = props.normalCount ?? 0;
				const noDataCount = props.noDataCount ?? 0;

				clusters.push({
					id: props.cluster_id,
					longitude: feature.geometry.coordinates[0],
					latitude: feature.geometry.coordinates[1],
					count: props.point_count,
					hasAlert: criticalCount > 0,
					normalCount,
					criticalCount,
					noDataCount,
					selectedCount: props.selectedCount ?? 0,
					clusterId: props.cluster_id,
				});
			} else {
				const infraIndex = props.index;
				if (infraIndex != null && filteredInfras[infraIndex]) {
					unclustered.push({ infrastructure: filteredInfras[infraIndex] });
				}
			}
		}

		const getExpansionZoom = (clusterId: number): number => {
			try {
				return index.getClusterExpansionZoom(clusterId);
			} catch {
				return clusterZoom + 2;
			}
		};

		setResult({ clusters, unclustered, getExpansionZoom });
	}, [filteredInfras, zoom, selectedInfraIds]);

	const getExpansionZoom = useCallback((clusterId: number): number => {
		try {
			return indexRef.current?.getClusterExpansionZoom(clusterId) ?? Math.floor(zoom) + 2;
		} catch {
			return Math.floor(zoom) + 2;
		}
	}, [zoom]);

	return {
		clusters: result.clusters,
		unclustered: result.unclustered,
		getExpansionZoom,
	};
}
